import { ApiError } from '@/shared/lib/api/api-client'

/** Failure codes `DELETE /api/Accounts/{id}` answers with (th_saas CU-90). */
export const AccountDeleteErrorCode = {
  NotFound: 'ACCOUNT_NOT_FOUND',
  Forbidden: 'ACCOUNT_DELETE_FORBIDDEN',
  Failed: 'ACCOUNT_DELETE_FAILED',
} as const

export type AccountDeleteErrorCode = (typeof AccountDeleteErrorCode)[keyof typeof AccountDeleteErrorCode]

export interface AccountDeleteError {
  /** Null when the failure carried no body or a code this screen does not know. */
  code: AccountDeleteErrorCode | null
  /** The backend's English text, used when the code has no translation. */
  message: string | null
}

const KNOWN_CODES: readonly string[] = Object.values(AccountDeleteErrorCode)

function isKnownCode(value: unknown): value is AccountDeleteErrorCode {
  return typeof value === 'string' && KNOWN_CODES.includes(value)
}

/** Reads the `{ errorCode, message }` body of a failed delete. */
export function readAccountDeleteError(error: unknown): AccountDeleteError {
  if (!(error instanceof ApiError) || !error.details) {
    return { code: null, message: null }
  }

  const { errorCode, message } = error.details
  return {
    code: isKnownCode(errorCode) ? errorCode : null,
    message: typeof message === 'string' && message.length > 0 ? message : null,
  }
}

/** The account was already gone — someone else deleted it, so the grid is stale. */
export function isAccountNotFound(error: unknown): boolean {
  return readAccountDeleteError(error).code === AccountDeleteErrorCode.NotFound
}
